import type { ReactNode } from 'react'
import { ArrowDownRight, ArrowUpRight, Info } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { mensajeErrorDeCarga } from '@/lib/permisos-guard'
import { cn, formatCurrency } from '@/lib/utils'

interface KpiCardProps {
  titulo: string
  /** Definición del número, que se muestra en el tooltip del título. */
  ayuda: string
  /** undefined mientras carga. null si no hay medición, y se muestra «—». */
  valor: number | null | undefined
  /** Variación % contra el período anterior. null muestra `sinVariacion`. */
  variacion: number | null | undefined
  /** Texto que reemplaza a la variación cuando es null. Por defecto, «Sin datos del período anterior». */
  sinVariacion?: string
  /** Si una suba es buena noticia. Define el color de la variación. */
  mejorSiSube: boolean
  isLoading: boolean
  isError: boolean
  /** Error de la consulta. Con un 42501 la tarjeta dice que falta el permiso. */
  error?: unknown
  detalle?: ReactNode
  /** Formato del valor. Por defecto, moneda. */
  formato?: (valor: number) => string
}

const formatPct = (valor: number) => `${Math.abs(valor).toLocaleString('es-AR', { maximumFractionDigits: 1 })}%`

type KpiVariacionProps = Pick<KpiCardProps, 'variacion' | 'mejorSiSube'> & { sinVariacion: string }

/** Línea de variación contra el período anterior, en verde o en rojo según si el cambio es bueno. */
function KpiVariacion({ variacion, sinVariacion, mejorSiSube }: KpiVariacionProps) {
  if (variacion == null) return <p className="text-xs text-muted-foreground">{sinVariacion}</p>

  const sube = variacion >= 0
  const color = sube === mejorSiSube ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-500'
  return (
    <p className={cn('text-xs flex items-center gap-1', color)}>
      {sube ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
      {formatPct(variacion)} contra el período anterior
    </p>
  )
}

/** Tarjeta de un KPI del dashboard: valor, variación contra el período anterior y líneas de detalle. */
export function KpiCard({
  titulo,
  ayuda,
  valor,
  variacion,
  sinVariacion = 'Sin datos del período anterior',
  mejorSiSube,
  isLoading,
  isError,
  error,
  detalle,
  formato = formatCurrency,
}: KpiCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
          {titulo}
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label={`Qué mide ${titulo}`} className="text-muted-foreground/70">
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{ayuda}</TooltipContent>
          </Tooltip>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        {isError ? (
          <p className="text-sm text-destructive">{mensajeErrorDeCarga(error)}</p>
        ) : isLoading || valor === undefined ? (
          <Skeleton className="h-8 w-40" />
        ) : (
          <>
            <p className="text-2xl font-bold">{valor === null ? '—' : formato(valor)}</p>
            <KpiVariacion variacion={variacion} sinVariacion={sinVariacion} mejorSiSube={mejorSiSube} />
            {detalle && <div className="text-xs text-muted-foreground space-y-0.5">{detalle}</div>}
          </>
        )}
      </CardContent>
    </Card>
  )
}
